import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Storefront, CaretLeft, Minus, Plus, Trash, ShoppingCart } from "@phosphor-icons/react";
import { readCart, setQty, removeFromCart, cartSubtotal } from "@/lib/shopCart";

const rp = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");

export default function ShopCart() {
  const nav = useNavigate();
  const [items, setItems] = useState(readCart());
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    document.title = "Keranjang — HalloGBS";
    const sync = () => setItems(readCart());
    window.addEventListener("shop-cart-changed", sync);
    return () => window.removeEventListener("shop-cart-changed", sync);
  }, []);

  // Harga & subtotal DIHITUNG ULANG oleh server (termasuk diskon aktif). Jangan percaya localStorage.
  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    api.post("/public/shop/quote", { items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })) })
      .then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [items]);

  const lineOf = (it) => (quote?.lines || []).find((l) => l.product_id === it.product_id && l.variant_id === it.variant_id);
  const subtotal = quote ? quote.subtotal : cartSubtotal();

  const checkout = () => {
    if (localStorage.getItem("patient_token")) nav("/checkout-belanja");
    else nav("/pasien/masuk?returnTo=/checkout-belanja");
  };

  return (
    <div className="min-h-screen bg-[#FAF7FF]" data-testid="shop-cart-page">
      <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-pink-100 bg-white/90 px-4 py-3 backdrop-blur">
        <Link to="/belanja" data-testid="cart-back" className="flex items-center gap-1.5 text-sm font-bold text-[#7C4DE0]"><CaretLeft size={18} weight="bold" /> Lanjut Belanja</Link>
        <h1 className="flex items-center gap-2 font-heading text-lg font-extrabold text-[#4C1D95]"><ShoppingCart size={20} weight="fill" className="text-[#DB2777]" /> Keranjang</h1>
        <span className="w-16" />
      </header>

      <div className="mx-auto max-w-2xl px-4 py-5 pb-32">
        {items.length === 0 ? (
          <div className="rounded-2xl border border-pink-100 bg-white p-10 text-center" data-testid="cart-empty">
            <Storefront size={40} weight="duotone" className="mx-auto text-[#C9B6F0]" />
            <p className="mt-2 text-sm text-slate-400">Keranjang masih kosong.</p>
            <Link to="/belanja" className="mt-3 inline-block rounded-xl bg-[#7C4DE0] px-4 py-2 text-xs font-bold text-white">Mulai Belanja</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <div key={`${it.product_id}-${it.variant_id}`} data-testid={`cart-item-${it.variant_id}`} className="flex gap-3 rounded-2xl border border-pink-100 bg-white p-3">
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-[#F3ECFF]">
                  {it.img_id ? (
                    <img src={`${API}/public/shop/products/${it.product_id}/image/${it.img_id}`} alt={it.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[#C9B6F0]"><Storefront size={28} weight="duotone" /></div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-sm font-bold text-slate-800">{it.name}</p>
                  <p className="text-xs text-slate-400">{it.variant_label}</p>
                  {(() => { const ln = lineOf(it); const disc = ln && ln.discount_amount > 0;
                    return (
                    <p className="mt-0.5 flex items-center gap-1.5 text-sm font-extrabold text-[#DB2777]">
                      {rp(ln ? ln.price_final : it.price)}
                      {disc && <span className="text-[11px] font-normal text-slate-400 line-through">{rp(ln.price)}</span>}
                      {disc && <span className="rounded-full bg-[#FCE7F3] px-1 text-[9px] font-extrabold text-[#DB2777]">-{ln.percent_off}%</span>}
                    </p>
                  ); })()}
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center rounded-lg border border-pink-100">
                      <button data-testid={`cart-minus-${it.variant_id}`} onClick={() => setQty(it.product_id, it.variant_id, it.qty - 1)} className="px-2.5 py-1.5 text-slate-500"><Minus size={13} weight="bold" /></button>
                      <span data-testid={`cart-qty-${it.variant_id}`} className="min-w-7 text-center text-sm font-bold">{it.qty}</span>
                      <button data-testid={`cart-plus-${it.variant_id}`} onClick={() => setQty(it.product_id, it.variant_id, it.qty + 1)} disabled={it.qty >= it.stock} className="px-2.5 py-1.5 text-slate-500 disabled:opacity-40"><Plus size={13} weight="bold" /></button>
                    </div>
                    <button data-testid={`cart-remove-${it.variant_id}`} onClick={() => removeFromCart(it.product_id, it.variant_id)} className="flex items-center gap-1 text-xs font-semibold text-rose-500"><Trash size={14} /> Hapus</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-pink-100 bg-white/95 px-4 py-3 backdrop-blur">
          <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
            <div>
              <p className="text-xs text-slate-400">Subtotal</p>
              <p data-testid="cart-subtotal" className="text-lg font-extrabold text-[#1E1B4B]">{rp(subtotal)}</p>
            </div>
            <button data-testid="cart-checkout" onClick={checkout} className="flex items-center gap-2 rounded-2xl bg-[#7C4DE0] px-6 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#6A38C7] active:scale-[0.99]">Checkout</button>
          </div>
        </div>
      )}
    </div>
  );
}
